import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  getAllCurrencyCodes,
  getCurrencyInfo,
  type CurrencyInfo
} from '../../constants/currencies';

interface CurrencySelectorProps {
  value: string;
  onChange: (currencyCode: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  showGermanNames?: boolean;
  'data-testid'?: string;
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Select currency...',
  disabled = false,
  error = false,
  showGermanNames = false,
  'data-testid': testId
}) => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get all currencies sorted alphabetically
  const sortedCurrencies = React.useMemo(() => {
    const allCodes = getAllCurrencyCodes();
    const currencies = allCodes
      .map(code => getCurrencyInfo(code))
      .filter((currency): currency is CurrencyInfo => Boolean(currency))
      .sort((a, b) => {
        // Sort by name in the current language
        const nameA = language === 'de' && a.localName ? a.localName : a.name;
        const nameB = language === 'de' && b.localName ? b.localName : b.name;
        return nameA.localeCompare(nameB, language === 'de' ? 'de' : 'en');
      });
    return currencies;
  }, [language]);

  // Get display value for selected currency
  const getDisplayValue = (currencyCode: string) => {
    if (!currencyCode) return '';
    return currencyCode;
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setSelectedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < sortedCurrencies.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && sortedCurrencies[selectedIndex]) {
          handleSelect(sortedCurrencies[selectedIndex].code);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelect = (currencyCode: string) => {
    onChange(currencyCode);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef} data-testid={testId}>
      <div
        className={`
          relative w-full cursor-pointer rounded-md border bg-white px-3 py-2 text-left 
          shadow-sm focus:outline-none focus:ring-1 review-input transition-colors
          ${error 
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
            : 'border-border-primary focus:border-indigo-500 focus:ring-indigo-500'
          }
          ${disabled ? 'bg-bg-secondary cursor-not-allowed' : 'hover:border-border-secondary'}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="currency-listbox"
      >
        <span className={`block truncate ${!value ? 'text-text-tertiary' : ''}`}>
          {value ? getDisplayValue(value) : placeholder}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronDown 
            className={`h-5 w-5 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true" 
          />
        </span>
      </div>

      {isOpen && (
        <div 
          id="currency-listbox"
          role="listbox"
          className="absolute z-50 mt-1 max-h-96 min-w-[320px] overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none review-input"
        >
          {sortedCurrencies.map((currency, index) => {
            const isSelected = selectedIndex === index;
            const isCurrentValue = currency.code === value;
            
            return (
              <div
                key={currency.code}
                className={`
                  relative cursor-pointer select-none py-2.5 pl-4 pr-10 text-sm
                  ${isSelected ? 'bg-indigo-600 text-white' : 'text-text-primary'}
                  ${isCurrentValue ? 'font-semibold' : 'font-normal'}
                  hover:bg-indigo-600 hover:text-white
                `}
                onClick={() => handleSelect(currency.code)}
                role="option"
                aria-selected={isCurrentValue}
              >
                <div className="flex items-center gap-3">
                  <span className={`font-mono ${isSelected ? 'text-white' : 'text-text-tertiary'} min-w-[2.5rem]`}>
                    {currency.code}
                  </span>
                  <div className="flex-1 flex items-center gap-2">
                    <span className="block">
                      {language === 'de' || showGermanNames ? currency.localName || currency.name : currency.name}
                    </span>
                    <span className={`text-xs font-mono ${isSelected ? 'text-indigo-200' : 'text-text-tertiary'}`}>
                      ({currency.symbol})
                    </span>
                  </div>
                </div>
                
                {isCurrentValue && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                    <Check className="h-5 w-5" aria-hidden="true" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;